import { IndexedDBService, type SyncOperation, type LocalEntity } from './IndexedDBService';
import { OfflineQueue } from './OfflineQueue';
import { pwaService } from './PWAService';
import type { BackendSyncOperationType, SyncResult, DeviceConfig } from './types';
import axios, { type ApiResponse } from '../../services/api/axios';

interface SyncRecord {
  id: string;
  updatedAt?: string;
  createdAt?: string;
  version?: number;
  [key: string]: unknown;
}

interface DownloadPayload {
  products: SyncRecord[];
  customers: SyncRecord[];
  stockItems: SyncRecord[];
  salesInvoices?: SyncRecord[];
  syncCursor?: string;
  serverTime?: string;
  pulledAt?: string;
}

interface PushOperationResult {
  localId: string;
  status: 'processed' | 'duplicate' | 'rejected';
  code?: string;
  message?: string | null;
  reason?: string;
  retryable?: boolean;
}

interface BatchStatus {
  batchId: string;
  status: string;
  resolution?: PushOperationResult[];
}

interface PushPayload {
  batch?: BatchStatus;
  batchId?: string;
  results?: PushOperationResult[];
}

const supportedOperationTypes: readonly BackendSyncOperationType[] = [
  'POS_CHECKOUT',
  'POS_SALE',
  'INVENTORY_ADJUSTMENT',
  'STOCKTAKE_COUNT',
  'STOCKTAKE_SUBMIT',
];

const SYNC_REQUEST_TIMEOUT = Number.parseInt(
  import.meta.env.VITE_SYNC_API_TIMEOUT || '60000',
  10,
);

export class SyncService {
  private db: IndexedDBService;
  private queue: OfflineQueue;
  private deviceId: string;
  private deviceConfig: DeviceConfig | null = null;
  private isOnline: boolean = navigator.onLine;
  private syncInProgress: boolean = false;
  private lastSyncTime: Date | null = null;
  private periodicSync?: ReturnType<typeof setInterval>;
  private unsubscribeNetwork?: () => void;
  private isDestroyed = false;
  private readonly ready: Promise<void>;
  private readonly configStorageKey: string;
  private readonly cursorKey: string;
  private deviceRegistered = false;
  private readonly onlineHandler = () => {
    this.isOnline = true;
    void this.performSync();
  };
  private readonly offlineHandler = () => {
    this.isOnline = false;
  };

  constructor(deviceId: string, tenantId: string) {
    this.deviceId = deviceId;
    this.db = new IndexedDBService(tenantId);
    this.queue = new OfflineQueue(this.db);
    this.configStorageKey = `zaytun_soft_sync_config:${tenantId}:${deviceId}`;
    this.cursorKey = `sync:${deviceId}`;
    this.ready = this.initialize();
    void this.ready.catch((error) => console.error('Failed to initialize sync:', error));
  }

  private async initialize(): Promise<void> {
    await this.db.initialize();
    if (this.isDestroyed) return this.db.close();
    await this.queue.initialize();
    const cursor = await this.db.getLastSync(this.cursorKey);
    this.lastSyncTime = cursor === null ? null : new Date(cursor);
    await this.loadDeviceConfig();
    if (this.isDestroyed) return this.db.close();
    if (this.isOnline) {
      try {
        await this.registerDevice();
      } catch (error) {
        console.warn('Failed to register device, will retry when syncing:', error);
      }
    }
    if (this.isDestroyed) return this.db.close();
    this.setupNetworkListeners();
    this.startPeriodicSync();
    if (this.isOnline) void this.performSync();
  }

  // تسجيل الجهاز في الخادم
  private async registerDevice(): Promise<void> {
    await axios.post(
      '/sync/devices/register',
      {
        deviceId: this.deviceId,
        deviceName: navigator.userAgent.slice(0, 255),
        deviceType: this.getDeviceType(),
        platform: 'web',
        appVersion: (import.meta.env.VITE_APP_VERSION ?? '1.0.0').slice(0, 50),
      },
      {
        timeout: 30000,
      },
    );

    this.deviceRegistered = true;
  }

  // Device settings are local; the backend has no device-config endpoint.
  private async loadDeviceConfig(): Promise<void> {
    const defaults: DeviceConfig = {
      deviceId: this.deviceId,
      syncEnabled: true,
      syncInterval: 15,
      maxBatchSize: 100,
      supportedEntities: ['Product', 'ProductVariant', 'Customer', 'StockItem', 'SalesInvoice', 'Payment'],
      offlineTimeout: 24,
    };
    this.deviceConfig = defaults;
    try {
      const stored = localStorage.getItem(this.configStorageKey);
      if (stored) {
        this.deviceConfig = this.normalizeDeviceConfig({ ...defaults, ...JSON.parse(stored) });
      }
    } catch (error) {
      console.warn('Failed to read local sync settings, using defaults:', error);
    }
  }

  private normalizeDeviceConfig(config: DeviceConfig): DeviceConfig {
    return {
      ...config,
      deviceId: this.deviceId,
      syncEnabled: typeof config.syncEnabled === 'boolean' ? config.syncEnabled : true,
      syncInterval: Number.isFinite(config.syncInterval) && config.syncInterval > 0 ? config.syncInterval : 15,
      maxBatchSize: Number.isFinite(config.maxBatchSize) && config.maxBatchSize >= 1 ? Math.floor(config.maxBatchSize) : 100,
      offlineTimeout: Number.isFinite(config.offlineTimeout) && config.offlineTimeout > 0 ? config.offlineTimeout : 24,
      supportedEntities: Array.isArray(config.supportedEntities) ? config.supportedEntities.filter((entity) => typeof entity === 'string') : [],
    };
  }

  // إعداد مستمعي الشبكة
  private setupNetworkListeners(): void {
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);

    // مراقبة تغييرات PWA Service
    this.unsubscribeNetwork = pwaService.onNetworkChange((status) => {
      this.isOnline = status.isOnline;
      if (this.isOnline) {
        this.performSync();
      }
    });
  }

  // بدء المزامنة الدورية
  private startPeriodicSync(): void {
    if (this.periodicSync) clearInterval(this.periodicSync);
    if (this.isDestroyed || !this.deviceConfig?.syncEnabled) return;

    const interval = (this.deviceConfig.syncInterval || 15) * 60 * 1000; // بالمللي ثانية

    this.periodicSync = setInterval(() => {
      if (!this.isDestroyed && this.isOnline && !this.syncInProgress) {
        this.performSync();
      }
    }, interval);
  }

  // أداء المزامنة الرئيسية
  async performSync(): Promise<SyncResult> {
    try {
      await this.ready;
    } catch (error) {
      return this.failedSync(error);
    }
    if (this.isDestroyed || !this.isOnline || this.syncInProgress || !this.deviceConfig?.syncEnabled) {
      return {
        success: false,
        changesUploaded: 0,
        changesDownloaded: 0,
        conflicts: 0,
        errors: ['Sync not available'],
        lastSyncTime: this.lastSyncTime || new Date(),
      };
    }

    this.syncInProgress = true;
    let changesUploaded = 0;
    let changesDownloaded = 0;
    let conflicts = 0;
    const errors: string[] = [];

    try {
      if (!this.deviceRegistered) await this.registerDevice();
      if (this.isDestroyed) return this.failedSync(new Error('Sync service destroyed'));
      const pendingOperations = await this.queue.getPendingOperations();
      const operations: SyncOperation[] = [];
      for (const operation of pendingOperations) {
        if (supportedOperationTypes.includes(operation.type as BackendSyncOperationType)) {
          operations.push(operation);
        } else {
          const message = `Unsupported queued sync operation: ${operation.type} (${operation.id})`;
          errors.push(message);
          await this.queue.updateOperationStatus(operation.id, false, message, false);
        }
      }

      const maxBatchSize = this.deviceConfig.maxBatchSize;
      for (let offset = 0; offset < operations.length; offset += maxBatchSize) {
        if (this.isDestroyed) return this.failedSync(new Error('Sync service destroyed'));
        const batchOperations = operations.slice(offset, offset + maxBatchSize);
        const uploadOperations = await Promise.all(batchOperations.map(async (operation) => {
          const key = await this.operationKey(operation.id);
          return {
            localId: key,
            type: operation.type,
            occurredAt: new Date(operation.timestamp).toISOString(),
            idempotencyKey: key,
            payload: operation.data,
          };
        }));
        const batchKey = await this.operationKey(JSON.stringify(uploadOperations.map((operation) => operation.idempotencyKey)));
        const response = await axios.post<ApiResponse<PushPayload>>(
          '/sync/push',
          {
            deviceId: this.deviceId,
            idempotencyKey: batchKey,
            operations: uploadOperations,
          },
          {
            timeout: SYNC_REQUEST_TIMEOUT,
          },
        );
        const payload = response.data.data;
        let results = payload.results;
        const batchId = payload.batch?.batchId ?? payload.batchId;
        if (!results && batchId) {
          results = (await this.getBatchStatus(batchId)).resolution;
        }
        if (!Array.isArray(results)) throw new Error('Sync push returned no operation results');

        for (let index = 0; index < batchOperations.length; index++) {
          const operation = batchOperations[index]!;
          const uploaded = uploadOperations[index]!;
          const result = results.find((entry) => entry.localId === uploaded.localId);
          if (result?.status === 'processed' || result?.status === 'duplicate') {
            await this.queue.markOperationsCompleted([operation.id]);
            changesUploaded++;
          } else if (result?.status === 'rejected') {
            const message = result.message ?? result.reason ?? result.code ?? 'Sync operation rejected';
            errors.push(`${operation.id}: ${message}`);
            conflicts++;
            await this.queue.updateOperationStatus(operation.id, false, message, result.retryable === true);
          } else {
            // Missing acknowledgements remain pending with the same idempotency key.
            errors.push(`Missing sync result for ${operation.id}`);
          }
        }
      }

      if (this.isDestroyed) return this.failedSync(new Error('Sync service destroyed'));
      if (!this.lastSyncTime) {
        const initial = await axios.get<ApiResponse<DownloadPayload>>(
          '/sync/initial-data',
          {
            timeout: SYNC_REQUEST_TIMEOUT,
          },
        );
        if (this.isDestroyed) return this.failedSync(new Error('Sync service destroyed'));
        changesDownloaded += await this.processDownloadedData(initial.data.data);
      }
      // The initial snapshot has no sales invoices; a first full pull supplies them.
      const response = await axios.get<ApiResponse<DownloadPayload>>(
        '/sync/pull',
        {
          params: this.lastSyncTime ? { since: this.lastSyncTime.toISOString() } : {},
          timeout: SYNC_REQUEST_TIMEOUT,
        },
      );
      const download = response.data.data;
      if (this.isDestroyed) return this.failedSync(new Error('Sync service destroyed'));
      changesDownloaded += await this.processDownloadedData(download);
      const timestamp = download.syncCursor ?? download.pulledAt ?? download.serverTime;
      if (!timestamp || !Number.isFinite(new Date(timestamp).getTime())) {
        throw new Error('Sync download returned an invalid cursor');
      }
      const lastSyncTime = new Date(timestamp);
      await this.db.setLastSync(this.cursorKey, lastSyncTime.getTime());
      this.lastSyncTime = lastSyncTime;

      return {
        success: errors.length === 0,
        changesUploaded,
        changesDownloaded,
        conflicts,
        errors,
        lastSyncTime: this.lastSyncTime,
      };
    } catch (error) {
      console.error('Sync failed:', error);
      return {
        ...this.failedSync(error),
        changesUploaded,
        changesDownloaded,
        conflicts,
        errors: [...errors, this.errorMessage(error)],
      };
    } finally {
      this.syncInProgress = false;
    }
  }

  // Variants are nested in products, and payments are nested in sales invoices.
  private async processDownloadedData(payload: DownloadPayload): Promise<number> {
    const groups: Array<{
      store: 'products' | 'customers' | 'inventory' | 'sales';
      records: SyncRecord[];
    }> = [
      { store: 'products', records: payload.products },
      { store: 'customers', records: payload.customers },
      { store: 'inventory', records: payload.stockItems },
      { store: 'sales', records: payload.salesInvoices ?? [] },
    ];
    let count = 0;
    for (const { store, records } of groups) {
      if (!Array.isArray(records)) throw new Error(`Invalid sync records for ${store}`);
      for (const record of records) {
        if (typeof record.id !== 'string') throw new Error(`Invalid sync record ID for ${store}`);
        const modified = record.updatedAt ?? record.createdAt ?? payload.pulledAt ?? payload.serverTime;
        const lastModified = modified ? new Date(modified).getTime() : Date.now();
        if (!Number.isFinite(lastModified)) throw new Error(`Invalid sync timestamp for ${store}`);
        const existing = await this.db.getEntity(store, record.id);
        const localEntity: LocalEntity = {
          id: record.id,
          data: { ...existing?.data, ...record },
          lastModified,
          version: typeof record.version === 'number' ? record.version : 1,
        };
        await this.db.saveEntity(store, localEntity);
        count++;
      }
    }
    return count;
  }

  private async operationKey(value: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  private errorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
      return error.message;
    }
    return 'Sync failed';
  }

  private failedSync(error: unknown): SyncResult {
    return {
      success: false,
      changesUploaded: 0,
      changesDownloaded: 0,
      conflicts: 0,
      errors: [this.errorMessage(error)],
      lastSyncTime: this.lastSyncTime ?? new Date(),
    };
  }

  // إضافة عملية للمزامنة
  async addOperation(
    operation: Omit<SyncOperation, 'id' | 'timestamp' | 'retryCount' | 'status' | 'type'> & {
      type: BackendSyncOperationType;
    },
  ): Promise<void> {
    await this.ready;
    if (this.isDestroyed) throw new Error('Sync service destroyed');
    if (!supportedOperationTypes.includes(operation.type as BackendSyncOperationType)) {
      throw new Error(`Unsupported sync operation: ${operation.type}`);
    }
    if (!operation.data || typeof operation.data !== 'object' || Array.isArray(operation.data)) {
      throw new Error('Sync operation payload must be an object');
    }
    await this.queue.addOperation(operation);

    // محاولة فورية للمزامنة إذا كان متصلاً
    if (this.isOnline && !this.syncInProgress) {
      setTimeout(() => this.performSync(), 1000); // تأخير قليل
    }
  }

  // مزامنة يدوية
  async syncNow(): Promise<SyncResult> {
    return this.performSync();
  }

  async waitUntilReady(): Promise<void> {
    await this.ready;
  }

  // الحصول على إحصائيات المزامنة
  async getSyncStats(): Promise<{
    isOnline: boolean;
    isSyncing: boolean;
    lastSyncTime: Date | null;
    pendingOperations: number;
    deviceConfig: DeviceConfig | null;
  }> {
    await this.ready;
    const pendingOperations = await this.queue.getPendingOperations();

    return {
      isOnline: this.isOnline,
      isSyncing: this.syncInProgress,
      lastSyncTime: this.lastSyncTime,
      pendingOperations: pendingOperations.length,
      deviceConfig: this.deviceConfig,
    };
  }

  async destroy(): Promise<void> {
    this.isDestroyed = true;
    if (this.periodicSync) clearInterval(this.periodicSync);
    this.unsubscribeNetwork?.();
    window.removeEventListener('online', this.onlineHandler);
    window.removeEventListener('offline', this.offlineHandler);
    await this.ready.catch(() => undefined);
    await this.db.close();
  }

  // تحديث إعدادات الجهاز
  async updateDeviceConfig(config: Partial<DeviceConfig>): Promise<void> {
    await this.ready;
    if (!this.deviceConfig) return;
    const updated = this.normalizeDeviceConfig({ ...this.deviceConfig, ...config });
    localStorage.setItem(this.configStorageKey, JSON.stringify(updated));
    this.deviceConfig = updated;
    this.startPeriodicSync();
  }

  async getServerStatus(): Promise<{
    activeDevices: number;
    pendingOperations: number;
    device?: { deviceId: string; isActive: boolean; revokedAt: string | null };
  }> {
    await this.ready;
    const response = await axios.get<ApiResponse<{
      activeDevices: number;
      pendingOperations: number;
      device?: { deviceId: string; isActive: boolean; revokedAt: string | null };
    }>>('/sync/status', { params: { deviceId: this.deviceId } });
    return response.data.data;
  }

  async getBatchStatus(batchId: string): Promise<BatchStatus> {
    const response = await axios.get<ApiResponse<BatchStatus>>(
      `/sync/batches/${encodeURIComponent(batchId)}`,
      {
        timeout: SYNC_REQUEST_TIMEOUT,
      },
    );
    return response.data.data;
  }

  // مساعد: تحديد نوع الجهاز
  private getDeviceType(): string {
    const ua = navigator.userAgent;

    if (/Android/i.test(ua)) return 'Android';
    if (/iPad|iPhone|iPod/i.test(ua)) return 'iOS';
    if (/Windows/i.test(ua)) return 'Windows';
    if (/Mac/i.test(ua)) return 'MacOS';
    if (/Linux/i.test(ua)) return 'Linux';

    return 'Unknown';
  }
}
